import React, { useState, useEffect } from 'react';
import './Changepass.css';
import { FiEye, FiEyeOff } from "react-icons/fi";
import Dashboardmy from '../Dashboard-component/Dashboard';
import { putAPI } from '../../util/Api';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { showToastErrorMsg, showToastSuccessMsg } from '../../store/toastMsg';

const ChangePassword = () => {
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth?.auth?.access_token);
  const login = useSelector((state) => state.auth?.auth?.access_token);
  const username = useSelector((state) => state.auth?.auth?.name);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false
  });

  const validatePassword = (password) => {
    const errors = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*]/.test(password)
    };
    setPasswordErrors(errors);
    return Object.values(errors).every(Boolean);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    
    // Validate new password when it changes
    if (name === 'new_password') {
      validatePassword(value);
    }
  };
  
  useEffect(() => {
    if (!login) {
      navigate("/login");
    }
  }, [login, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate current and new password are not same
    if (formData.old_password === formData.new_password) {
      setError('New password must be different from current password');
      return;
    }

    // Validate passwords match
    if (formData.new_password !== formData.confirm_password) {
      setError('New password and confirm password do not match');
      return;
    }

    // Validate password requirements
    if (!validatePassword(formData.new_password)) {
      setError('Password does not meet requirements');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        old_password: formData.old_password,
        new_password: formData.new_password
      };

      const response = await putAPI(accessToken, '/user/reset_password', payload);

      if (response?.statusCode === 200) {
        showToastSuccessMsg('Password reset successful');
        // Reset form
        setFormData({
          old_password: '',
          new_password: '',
          confirm_password: ''
        });
        // Reset password errors
        setPasswordErrors({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          specialChar: false
        });
      } else {
        showToastErrorMsg(response?.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      if (error?.response?.status === 401) {
        showToastErrorMsg('Session expired. Please login again');
        navigate('/login');
      } else {
        showToastErrorMsg('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${username} | Change password`}</title>
        <meta name="description" content={`user Change password page of Shambasync`} />
      </Helmet>
      {/* <ToastContainer autoClose={3000} /> */}

      <div className='show-content'>
        <Dashboardmy />

        <div className="change-password-container">
          <div className="change-password-heading">Change Password</div>

          <form className="change-password-form" onSubmit={handleSubmit}>
            {/* Current Password */}
            <div className="form-group-change full-width">
              <label className="form-label">Current Password</label>
              <div className="input-wrapper">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  name="old_password"
                  value={formData.old_password}
                  onChange={handleInputChange}
                  placeholder="Current Password"
                  className="input-field-change"
                  required
                />
                <span className="eye-icon-dashboard" onClick={() => setShowCurrent(!showCurrent)}>
                  {showCurrent ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
            </div>

            {/* New Password */}
            <div className="form-group-change half-width">
              <label className="form-label">New Password</label>
              <div className="input-wrapper">
                <input
                  type={showNew ? 'text' : 'password'}
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  placeholder="New Password"
                  className="input-field-change"
                  required
                />
                <span className="eye-icon-dashboard" onClick={() => setShowNew(!showNew)}>
                  {showNew ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
              {formData.new_password && (
                <div className="password-requirements">
                  <p>Password must contain:</p>
                  <ul>
                    <li className={passwordErrors.length ? "valid" : "invalid"}>
                      At least 8 characters
                    </li>
                    <li className={passwordErrors.uppercase ? "valid" : "invalid"}>
                      At least 1 uppercase letter
                    </li>
                    <li className={passwordErrors.lowercase ? "valid" : "invalid"}>
                      At least 1 lowercase letter
                    </li>
                    <li className={passwordErrors.number ? "valid" : "invalid"}>
                      At least 1 number
                    </li>
                    <li className={passwordErrors.specialChar ? "valid" : "invalid"}>
                      At least 1 special character (!@#$%^&*)
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="form-group-change half-width">
              <label className="form-label">Confirm Password</label>
              <div className="input-wrapper">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  placeholder="Confirm Password"
                  className="input-field-change"
                  required
                />
                <span className="eye-icon-dashboard" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="save-btn-wrapper">
              <button
                className="save-btn"
                type="submit"
                disabled={loading || !Object.values(passwordErrors).every(Boolean)}
              >
                {loading ? 'Updating...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
